import { forwardRef, useContext } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";

import { LanguageContext } from "src/contexts/language.context";
import { InvoiceContext } from "src/contexts/invoice.context";

import "react-datepicker/dist/react-datepicker.css";
import "./table.style.scss";
import useTranslate from "src/hooks/use-translate";

registerLocale("fr", fr);

const COLUMNS = {
	Date: {
		columnName: "date",
		inputType: "date",
		placeholder: "Nan/Nan/Nan",
	},
	Explanation: {
		columnName: "explanation",
		inputType: "text",
		placeholder: "N/A",
	},
	Revenue: {
		columnName: "revenue",
		inputType: "number",
		placeholder: "N/A",
		getValue: (value) => {
			return value.toLocaleString("en-US", {
				maximumFractionDigits: 3,
				minimumFractionDigits: 0,
			});
		},
	},
	Expense: {
		columnName: "expense",
		inputType: "number",
		placeholder: "N/A",
		getValue: (value) => {
			return value.toLocaleString("en-US", {
				maximumFractionDigits: 3,
				minimumFractionDigits: 0,
			});
		},
	},
	Balance: {
		columnName: "balance",
		inputType: "number",
		placeholder: "N/A",
		getValue: (value) => {
			return value.toLocaleString("en-US", {
				maximumFractionDigits: 3,
				minimumFractionDigits: 0,
			});
		},
	},
	"": {
		columnName: "",
		inputType: "hidden",
		placeholder: "",
	},
};

const NewRow = ({ newRow, removeRow, handleChange, language }) => {
	const balance = (newRow.Revenue ?? 0) - (newRow.Expense ?? 0);
	// const balance = newRow.Revenue && newRow.Expense ? newRow.Revenue - newRow.Expense : null;

	const CustomDatePicker = forwardRef(({ value, onClick, placeholder }, ref) => {
		return (
			<button className="date-picker" onClick={onClick}>
				{value === "" ? placeholder : value}
			</button>
		);
	});

	return (
		<tr className="table-row new-row">
			{Object.keys(newRow).map((columnName) => {
				const isDisabled =
					(columnName === "Revenue" && newRow.Expense && newRow.Expense?.length > 0) ||
					(columnName === "Expense" && newRow.Revenue && newRow.Revenue?.length > 0);
				return (
					<td key={columnName}>
						{columnName === "Date" ? (
							<DatePicker
								dateFormat="dd/MM/yyyy"
								selected={newRow[columnName]}
								onChange={handleChange}
								locale={language}
								customInput={<CustomDatePicker />}
								placeholderText={COLUMNS.Date.placeholder}
							/>
						) : (
							<input
								className="new-row-input"
								disabled={isDisabled}
								name={columnName}
								type={COLUMNS[columnName].inputType}
								value={
									COLUMNS[columnName].getValue
										? COLUMNS[columnName].getValue(newRow[columnName] ?? "")
										: newRow[columnName] ?? ""
								}
								onChange={handleChange}
								placeholder={COLUMNS[columnName].placeholder}
							/>
						)}
					</td>
				);
			})}
			<td className={`${balance > 0 ? "positive" : balance < 0 ? "negative" : ""}`}>
				{balance ? COLUMNS["Balance"].getValue(balance) : ""}
			</td>
			<td onClick={removeRow} className="remove-button">
				X
			</td>
		</tr>
	);
};

const Table = ({ editable = true }) => {
	const { language } = useContext(LanguageContext);
	const {
		shownInvoices,
		newRows,
		filteredNewRows,
		addNewRow,
		removeNewRow,
		removeRow,
		changeOnNewRow,
		save,
	} = useContext(InvoiceContext);

	const t = useTranslate("MySafe");

	const handleNewButton = () => {
		addNewRow();
	};

	const handleRemoveNewRow = (index) => {
		removeNewRow(index);
	};

	const handleRemoveRow = (index) => {
		// TODO: delete row
		removeRow(index);
	};

	const handleSave = () => {
		save();
	};

	const handleChange = (event, index) => {
		changeOnNewRow(event, index);
	};

	return (
		<div className="invoice-table-container">
			<table className="invoice-table">
				<thead>
					<tr className="table-row">
						{Object.values(COLUMNS).map(({ columnName }) => {
							return <th key={columnName}>{t(columnName)}</th>;
						})}
					</tr>
				</thead>
				<tbody>
					{newRows.map((row, index) => (
						<NewRow
							key={index}
							newRow={row}
							removeRow={() => handleRemoveNewRow(index)}
							handleChange={(event) => handleChange(event, index)}
							language={language}
						/>
					))}
					{shownInvoices.map((row, index) => {
						return (
							<tr
								key={index}
								className={`table-row ${index === shownInvoices.length - 1 && "last-child"}`}
							>
								{Object.keys(row).map((columnName) => {
									return (
										<td key={columnName} className={columnName}>
											{columnName === "Date"
												? format(new Date(row[columnName]), "dd/MM/yyyy")
												: row[columnName] ?? COLUMNS[columnName].placeholder}
										</td>
									);
								})}
								<td
									className={`${
										(row.Revenue ?? 0) - (row.Expense ?? 0) > 0
											? "positive"
											: (row.Revenue ?? 0) - (row.Expense ?? 0) < 0
											? "negative"
											: ""
									}`}
								>
									{((row.Revenue ?? 0) - (row.Expense ?? 0)).toLocaleString("en-US", {
										maximumFractionDigits: 3,
										minimumFractionDigits: 0,
									})}
								</td>
								{editable && (
									<td
										onClick={() => {
											handleRemoveRow(index);
										}}
										className="remove-button"
									>
										X
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>
			<div onClick={handleNewButton} className="new-file-button">
				<div className="text">+ {t("new")}</div>
			</div>
			{editable && (
				<div className="save-button-container">
					<button
						disabled={filteredNewRows.length === 0}
						className={`save-button ${filteredNewRows.length === 0 && "disabled"}`}
						onClick={handleSave}
					>
						{t("save")}
					</button>
				</div>
			)}
		</div>
	);
};

export default Table;
